// @ts-nocheck
/**
 * The one basic rich-text editor for operator-written text that leaves the
 * admin area: the Compose email body and the public schedule note.
 *
 * Toolbar: bold, italic, bulleted list, numbered list, link, View HTML.
 * Nothing else. Pasting from Word / Google Docs / an email keeps bold,
 * italic, lists, links and paragraphs and drops everything else (mso-*,
 * fonts, sizes, colours, classes). Merge fields like {{coach_first_name}}
 * are plain text to the editor, so they pass through untouched.
 *
 * variant="email" emits HTML with inline styles only (no classes), so what
 * the editor shows is what lands in the inbox.
 */
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Bold, Italic, List, ListOrdered, Link2, Code2 } from "lucide-react";
import DOMPurify from "dompurify";

export const NOTE_ALLOWED_TAGS = ["p", "br", "strong", "b", "em", "i", "u", "ul", "ol", "li", "a"];

/** Sanitise operator HTML for public rendering. */
export function sanitizeNoteHtml(html: string | null | undefined): string {
  if (!html) return "";
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: NOTE_ALLOWED_TAGS,
    ALLOWED_ATTR: ["href", "target", "rel"],
  });
}

/** True when the HTML holds no visible text. */
export function isEmptyNoteHtml(html: string | null | undefined): boolean {
  if (!html) return true;
  const text = html.replace(/<br\s*\/?>/gi, "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
  return text.length === 0;
}

const EMAIL_STYLES: Record<string, string> = {
  p: "margin:0 0 12px 0;",
  ul: "margin:0 0 12px 0;padding-left:24px;",
  ol: "margin:0 0 12px 0;padding-left:24px;",
  li: "margin:0 0 4px 0;",
  a: "color:#002E62;text-decoration:underline;",
};

/** Inline the few styles an email needs; strip classes. */
export function toEmailSafeHtml(html: string): string {
  if (!html || typeof DOMParser === "undefined") return html;
  const doc = new DOMParser().parseFromString(`<div id="root">${html}</div>`, "text/html");
  const root = doc.getElementById("root")!;
  root.querySelectorAll("*").forEach((el) => {
    el.removeAttribute("class");
    const tag = el.tagName.toLowerCase();
    let style = EMAIL_STYLES[tag] ?? "";
    if (tag === "p" && el.parentElement?.tagName.toLowerCase() === "li") style = "margin:0;";
    if (style) el.setAttribute("style", style);
    else el.removeAttribute("style");
  });
  return root.innerHTML;
}

/** Clean pasted HTML from Word / Google Docs before the editor parses it. */
export function cleanPastedHtml(html: string): string {
  if (typeof DOMParser === "undefined") return html;
  const stripped = html
    .replace(/<!--\[if[\s\S]*?<!\[endif\]-->/gi, "")
    .replace(/<!--[\s\S]*?-->/g, "")
    .replace(/<\/?o:p[^>]*>/gi, "");
  const doc = new DOMParser().parseFromString(stripped, "text/html");
  doc.querySelectorAll("style, meta, link, script, title, xml").forEach((n) => n.remove());
  const unwrap = (el: Element) => {
    const parent = el.parentNode;
    if (!parent) return;
    while (el.firstChild) parent.insertBefore(el.firstChild, el);
    parent.removeChild(el);
  };
  // Google Docs wraps the whole paste in <b style="font-weight:normal">.
  doc.querySelectorAll('b[id^="docs-internal-guid"], b[style*="font-weight:normal"], b[style*="font-weight: normal"]').forEach(unwrap);
  // Styled spans -> semantic marks, so bold/italic survive the style strip.
  doc.querySelectorAll("span").forEach((span) => {
    const s = (span.getAttribute("style") || "").toLowerCase().replace(/\s/g, "");
    const bold = /font-weight:(bold|[6-9]00)/.test(s);
    const italic = s.includes("font-style:italic");
    let inner: Element = span;
    if (bold) { const b = doc.createElement("strong"); while (inner.firstChild) b.appendChild(inner.firstChild); inner.appendChild(b); inner = b; }
    if (italic) { const i = doc.createElement("em"); while (inner.firstChild) i.appendChild(inner.firstChild); inner.appendChild(i); }
  });
  // Word list paragraphs (MsoListParagraph) -> <ul><li>.
  let currentList: Element | null = null;
  Array.from(doc.body.querySelectorAll("p")).forEach((p) => {
    const cls = p.getAttribute("class") || "";
    const style = p.getAttribute("style") || "";
    if (/MsoListParagraph/i.test(cls) || /mso-list:/i.test(style)) {
      const ordered = /^\s*\d+[.)]/.test(p.textContent || "");
      p.innerHTML = p.innerHTML.replace(/^(\s|&nbsp;)*([·•▪o§]|\d+[.)])(\s|&nbsp;)+/, "");
      const li = doc.createElement("li");
      li.innerHTML = p.innerHTML;
      const prev = p.previousElementSibling;
      if (!currentList || prev !== currentList) {
        currentList = doc.createElement(ordered ? "ol" : "ul");
        p.parentNode!.insertBefore(currentList, p);
      }
      currentList.appendChild(li);
      p.remove();
    } else {
      currentList = null;
    }
  });
  doc.body.querySelectorAll("*").forEach((el) => {
    el.removeAttribute("style");
    el.removeAttribute("class");
    el.removeAttribute("lang");
  });
  return doc.body.innerHTML;
}

/** Elements this editor cannot hold. Their presence means editing would lose layout. */
const RICH_LAYOUT = /<(table|img|style|div[^>]*style|h[1-6]|hr|font|center)\b/i;

/**
 * A designed template marks the only part an operator should change with
 * <div data-rle-editable="message">. Split the body into the fixed frame
 * before it, the editable inner HTML, and the fixed frame after it.
 * Returns null when there is no (well-formed) marked region.
 */
export function splitEditableRegion(html: string | null | undefined): { before: string; inner: string; after: string } | null {
  if (!html) return null;
  const open = /<div\b[^>]*\bdata-rle-editable=["']message["'][^>]*>/i.exec(html);
  if (!open) return null;
  const start = open.index + open[0].length;
  const tag = /<(\/?)div\b[^>]*>/gi;
  tag.lastIndex = start;
  let depth = 1;
  let m: RegExpExecArray | null;
  while ((m = tag.exec(html))) {
    depth += m[1] ? -1 : 1;
    if (depth === 0) {
      return { before: html.slice(0, start), inner: html.slice(start, m.index), after: html.slice(m.index) };
    }
  }
  return null;
}

const SLOT = '<div data-rle-slot="1"></div>';

interface Props {
  value: string;
  onChange: (html: string) => void;
  variant?: "email" | "note";
  placeholder?: string;
  minHeight?: string;
  ariaLabel?: string;
}

export function BasicRichTextEditor({ value, onChange, variant = "note", placeholder, minHeight = "180px", ariaLabel }: Props) {
  const [htmlMode, setHtmlMode] = useState(false);
  const [layoutWarning, setLayoutWarning] = useState(false);
  const lastEmitted = useRef<string>(value || "");
  // Fixed frame around a marked message region; null = whole body is editable.
  const initialRegion = splitEditableRegion(value);
  const [frame, setFrame] = useState<{ before: string; after: string } | null>(
    initialRegion ? { before: initialRegion.before, after: initialRegion.after } : null,
  );
  const frameRef = useRef(frame);
  frameRef.current = frame;
  const frameHost = useRef<HTMLDivElement>(null);
  const [slotEl, setSlotEl] = useState<HTMLElement | null>(null);

  const emit = (raw: string) => {
    const f = frameRef.current;
    if (f) {
      // Only the region round-trips through the editor; the frame is never rewritten.
      const inner = raw === "<p></p>" ? "" : raw;
      const out = f.before + inner + f.after;
      lastEmitted.current = out;
      onChange(out);
      return;
    }
    const out = raw === "<p></p>" ? "" : variant === "email" ? toEmailSafeHtml(raw) : raw;
    lastEmitted.current = out;
    onChange(out);
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false, blockquote: false, codeBlock: false, code: false,
        horizontalRule: false, strike: false, underline: false, link: false,
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { target: "_blank", rel: "noopener noreferrer" },
        // Merge-field links like {{magic_link}} must survive.
        isAllowedUri: (url, ctx) => /\{\{\s*\w+\s*\}\}/.test(url) || ctx.defaultValidate(url),
      }),
    ],
    content: initialRegion ? initialRegion.inner : value || "",
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-w-none focus:outline-none px-3 py-2 text-sm text-foreground " +
          "[&_a]:text-primary [&_a]:underline [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:my-2",
        style: `min-height: ${minHeight}`,
        "aria-label": ariaLabel ?? "Rich text editor",
        "data-placeholder": placeholder ?? "",
      },
      transformPastedHTML: cleanPastedHtml,
    },
    onUpdate: ({ editor }) => emit(editor.getHTML()),
  });

  // Sync external value (template chosen, reset after send).
  useEffect(() => {
    if (!editor) return;
    const incoming = value || "";
    if (incoming === lastEmitted.current) return;
    lastEmitted.current = incoming;
    const region = splitEditableRegion(incoming);
    if (region) {
      setFrame({ before: region.before, after: region.after });
      frameRef.current = { before: region.before, after: region.after };
      setLayoutWarning(false);
      setHtmlMode(false);
      editor.commands.setContent(region.inner, { emitUpdate: false });
      return;
    }
    setFrame(null);
    frameRef.current = null;
    const rich = RICH_LAYOUT.test(incoming);
    setLayoutWarning(rich);
    if (rich) setHtmlMode(true);
    editor.commands.setContent(incoming, { emitUpdate: false });
  }, [value, editor]);

  // Render the fixed frame read-only and mount the editor into its slot.
  const frameHtml = frame
    ? DOMPurify.sanitize(frame.before + SLOT + frame.after, { ADD_ATTR: ["data-rle-slot", "target"] })
    : "";
  useLayoutEffect(() => {
    if (!frame || htmlMode) { setSlotEl(null); return; }
    setSlotEl(frameHost.current?.querySelector<HTMLElement>("[data-rle-slot]") ?? null);
  }, [frameHtml, htmlMode]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("Link address (leave empty to remove)", prev ?? "https://");
    if (url === null) return;
    if (url === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const toggleHtml = () => {
    if (htmlMode) {
      // Back to the editor: parse the HTML as typed.
      const region = splitEditableRegion(value);
      if (region) {
        setFrame({ before: region.before, after: region.after });
        frameRef.current = { before: region.before, after: region.after };
        editor.commands.setContent(region.inner, { emitUpdate: false });
        lastEmitted.current = value || "";
        setLayoutWarning(false);
      } else {
        setFrame(null);
        frameRef.current = null;
        editor.commands.setContent(value || "", { emitUpdate: false });
        setLayoutWarning(false);
        emit(editor.getHTML());
      }
    }
    setHtmlMode(!htmlMode);
  };

  const Btn = ({ onClick, active, title, children, disabled }: any) => (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={!!active}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={"p-1.5 rounded text-foreground hover:bg-accent disabled:opacity-40 " + (active ? "bg-accent text-accent-foreground" : "")}
    >
      {children}
    </button>
  );

  return (
    <div className="rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring" data-testid="basic-rich-editor">
      <div className="flex flex-wrap items-center gap-0.5 px-1 py-1 border-b border-border bg-muted/30 rounded-t-md">
        <Btn title="Bold" disabled={htmlMode} active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="w-3.5 h-3.5" /></Btn>
        <Btn title="Italic" disabled={htmlMode} active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="w-3.5 h-3.5" /></Btn>
        <span className="w-px h-4 bg-border mx-1" />
        <Btn title="Bulleted list" disabled={htmlMode} active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="w-3.5 h-3.5" /></Btn>
        <Btn title="Numbered list" disabled={htmlMode} active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="w-3.5 h-3.5" /></Btn>
        <Btn title="Link" disabled={htmlMode} active={editor.isActive("link")} onClick={setLink}><Link2 className="w-3.5 h-3.5" /></Btn>
        <span className="flex-1" />
        <button
          type="button"
          onClick={toggleHtml}
          aria-pressed={htmlMode}
          className={"flex items-center gap-1 px-2 py-1 rounded text-xs text-muted-foreground hover:bg-accent " + (htmlMode ? "bg-accent text-accent-foreground" : "")}
        >
          <Code2 className="w-3.5 h-3.5" /> {htmlMode ? "Back to editor" : "View HTML"}
        </button>
      </div>
      {htmlMode ? (
        <>
          {layoutWarning && (
            <p className="px-3 pt-2 text-xs text-muted-foreground">
              This text has layout the editor can't show (tables, images or headings), so it opened as HTML. Switching to the editor will simplify it.
            </p>
          )}
          <textarea
            value={value}
            onChange={(e) => { lastEmitted.current = e.target.value; onChange(e.target.value); }}
            aria-label="HTML source"
            className="w-full bg-background px-3 py-2 text-xs font-mono text-foreground focus:outline-none rounded-b-md"
            style={{ minHeight }}
          />
        </>
      ) : frame ? (
        <div className="bg-muted/20 p-2 sm:p-4 rounded-b-md overflow-x-auto" data-testid="region-editor">
          <p className="text-xs text-muted-foreground mb-2">
            Only the highlighted message is editable. The header, details, button and sign-off stay as designed.
          </p>
          <div
            ref={frameHost}
            className="select-none [&_a]:pointer-events-none [&_[data-rle-slot]]:select-text [&_[data-rle-slot]]:rounded [&_[data-rle-slot]]:outline [&_[data-rle-slot]]:outline-2 [&_[data-rle-slot]]:outline-dashed [&_[data-rle-slot]]:outline-primary [&_[data-rle-slot]]:bg-background"
            aria-hidden={false}
            dangerouslySetInnerHTML={{ __html: frameHtml }}
          />
          {slotEl && createPortal(<EditorContent editor={editor} />, slotEl)}
        </div>
      ) : (
        <EditorContent editor={editor} />
      )}
    </div>
  );
}

/** Public, sanitised render. Renders nothing when empty. */
export function NoteHtml({ html, className = "" }: { html?: string | null; className?: string }) {
  if (isEmptyNoteHtml(html)) return null;
  return (
    <div
      className={`prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_a]:underline ${className}`}
      dangerouslySetInnerHTML={{ __html: sanitizeNoteHtml(html) }}
    />
  );
}

export default BasicRichTextEditor;
